import React, { Fragment, useEffect, useMemo, useState } from "react";
import TransitionOnLoad from "Components/TransitionOnLoad";
import { Button, Card, CardBody, CardFooter, Col, Row } from "reactstrap";
import PageHeading from "Layout/AppMain/PageHeading";
import Loader from "react-loaders";
import { ReactComponent as AddIcon } from "../../../../assets/utils/images/svgs/addBlue.svg";
import axiosInstance from "Api";
import Algemeen from "./BesturenView/Algemeen";
import StandaardInstellingenReiskosten from "./BesturenView/Standaard_instellingen_reiskosten";
import Vervangersprotocol from "./BesturenView/Vervangersprotocol";
import Coordinatoren from "./BesturenView/Coordinatoren";
import { toast } from "react-toastify";
import { debounce } from "../../../../helperFunctions/index"
import PDFPopup from "./BesturenView/PdfUploadModal";
import ReporterModal from "./BesturenView/ReporterModal";
import BesturenService from "Api/Instellingen/Besturen";
import { useApiMutation } from "services/useApiMutation";
import useSWR from "swr";

const validationRules = {
  Email: {
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    errorMessage: "Ongeldig e-mailadres"
  },
  Website: {
    pattern: /^(https?:\/\/)?([\w-]+(\.[\w-]+)+.*)$/i,
    errorMessage: "Ongeldige website-URL"
  },
};

const Besturen = () => {
  const [communityId, setCommunityId] = useState('');
  const [openFileUploadModal, setOpenFileUploadModal] = useState(false);
  const [errorField, setErrorField] = useState('');
  const [file, setFile] = useState("");
  const [openSearchModal, setOpenSearchModal] = useState(false);
  const [isPdfUploading, setisPdfUploading] = useState(false);
  const { trigger: saveBesturenMutation } = useApiMutation();
  const { trigger: reporterMutation } = useApiMutation();
  const { trigger: uploadFileMutation } = useApiMutation();

  const {
    data: bestuurResponse,
    mutate: refreshBestuurData,
  } = useSWR(
    communityId
      ? ["besturen", communityId]
      : null,
    ([, communityId]) =>
      BesturenService.fetchBesturen(communityId)
  );

  const bestuurList =
    bestuurResponse?.data?.data;

  const {
    data: communityResponse,
    isLoading: communityLoading,
  } = useSWR(
    "organizations-list",
    BesturenService.getCommunityList
  );

  const communityList = communityResponse?.data?.data || [];
  useEffect(() => {
    if (!communityId && communityList.length) {
      setCommunityId(communityList[0].iCommunityId);
    }
  }, [communityList, communityId]);

  const orgName = useMemo(() => {
    return communityList?.find(item => item.iCommunityId === Number(communityId))?.name || ""
  }, [communityId, communityList]);

  const callApi = (field, value, setFieldError = () => { }, errorField = "", setLoadingKeys) => {
    if (value !== "" && errorField !== "") {
      const rule = validationRules[errorField];

      if (rule && !rule.pattern.test(value)) {
        if (typeof setFieldError === "function") {
          setFieldError(field, rule.errorMessage);
          toast.dismiss();
          toast.error(rule.errorMessage);
        }
        return;
      }
    }

    if (field === "vPrice") {
      if (value < 0 || value > 99.99) {
        setFieldError(field, "De waarde moet een decimal getal tussen 0 en 99.99 liggen");
        toast.dismiss();
        toast.error("De waarde moet tussen 0 en 99.99 liggen");
        return
      }
    }

    const data = {
      iCommunityId: communityId,
      ...(field ? { attr: field } : {}),
      [field]: value,
    }
    saveBesturenMutation(() =>
      BesturenService.saveBesturen(data)
    )
      .then(response => {
        if (response?.status) {
          refreshBestuurData();

          if (errorField) {
            toast.dismiss();
            toast.success(errorField + " succesvol bijgewerkt.");
          }

          return;
        }
        if (!response?.status) {
          setErrorField(field);
          const errors = response.data[field];
          if (Array.isArray(errors)) {
            toast.error(errors.join(','));
          } else {
            toast.error(errors);
          }
        }
      })
      .catch(error => {
        console.error('API error:', error);
        toast.dismiss();
        toast.error("serverfout")
      }).finally(() => {
        if (setLoadingKeys) setLoadingKeys(prev => ({ ...prev, [field]: false }));
      });
  };

  const debouncedApiCall = debounce(callApi, 500);

  const handleFieldChange = (field, value, setFieldError = {}, errorField = "") => {
    setErrorField('');
    debouncedApiCall(field, value, setFieldError, errorField);
  };

  const handleIconFieldChange = (field, value, setLoadingKeys) => {
    setErrorField('');
    callApi(field, value, null, null, setLoadingKeys);
  };

  const togglePDFmodle = () => {
    setOpenFileUploadModal(false);
    setFile("");
  }


  const {
    data: reporterResponse,
    mutate: refreshReporterData,
    isLoading: isReporterLoading,
  } = useSWR(
    communityId
      ? ["reporters", communityId]
      : null,
    ([, communityId]) =>
      BesturenService.getReporterList(communityId)
  );
  const reporterData = reporterResponse?.data?.data || {};

  const {
    data: coordinatorResponse,
    mutate: refreshCoordinatorData,
  } = useSWR(
    communityId
      ? ["coordinators", communityId]
      : null,
    ([, communityId]) =>
      BesturenService.getCoordinatorList(
        communityId
      )
  );

  const coordinatorData =
    coordinatorResponse?.data?.data || [];

  const addOrDeleteMelderHandler = async (type, iAdminId) => {
    const data = {
      iAdminId,
      iCommunityId: communityId,
      type
    }

    reporterMutation(() => BesturenService.reporterListAddOrRemove(data)).then((response) => {
      if (response?.data?.status) {
        refreshReporterData();
      }
    })
  }

  const handleFileSave = () => {
    let formData = new FormData();
    formData.append('iAdminId', bestuurList?.iAdminId)
    formData.append('iCommunityId', communityId)
    formData.append('file', file);
    setisPdfUploading(true);

    uploadFileMutation(() => BesturenService.saveFile(formData)).then((response) => {
      if (response?.status) {
        toast.success('Bestand opgeslagen');
        togglePDFmodle();
        refreshReplacementProtocol();
        setisPdfUploading(false);
      } else {
        toast.error('Fout bij het opslaan van het bestand');
      }
    }).catch((error) => toast.error('Fout bij het opslaan van het bestand'))
      .finally(() => {
        setisPdfUploading(false);
      });
  }

  const {
    data: replacementResponse,
    mutate: refreshReplacementProtocol,
  } = useSWR(
    communityId
      ? ["replacementProtocol", communityId]
      : null,
    ([, communityId]) =>
      BesturenService.getReplacementProtocol(
        communityId
      )
  );

  const replacementProtocolData =
    replacementResponse?.data?.data || [];

  return (
    <Fragment>
      <PageHeading orgName={orgName} />
      <TransitionOnLoad>
        <Row className="mb-5">
          <Col>
            {
              communityLoading ||
                (communityId && !bestuurResponse) ?
                <Card className="main-card mb-3">
                  <CardBody className="p-3">
                    <Loader type="ball-grid-pulse" className={'mt-3 d-flex mx-auto justify-content-center'} />
                  </CardBody>
                </Card>
                :
                <div className='row contract-typen-view besturen-view  mb-5'>
                  <div className='col-xl-6 p-2'>

                    <Algemeen data={bestuurList} changeHandler={handleFieldChange} handleIconFieldChange={handleIconFieldChange} errorField={errorField} />

                  </div>
                  <div className='col-xl-6 p-2'>
                    <StandaardInstellingenReiskosten data={bestuurList} changeHandler={handleFieldChange} handleIconFieldChange={handleIconFieldChange} errorField={errorField} />
                  </div>
                  <div className='col-xl-6 p-2'>
                    <Vervangersprotocol changeHandler={handleFieldChange} replacementProtocol={refreshReplacementProtocol} replacementProtocolData={replacementProtocolData} />
                  </div>
                  <div className='col-xl-6 p-2'>
                    <Coordinatoren data={bestuurList} refreshReporterData={refreshReporterData} changeHandler={handleFieldChange} errorField={errorField} communityId={communityId} coordinatorData={coordinatorData} refreshCoordinatorData={refreshCoordinatorData} addOrDeleteMelderHandler={addOrDeleteMelderHandler} />
                  </div>
                </div>
            }
          </Col>
        </Row>

      </TransitionOnLoad>
      <div className="chat-layout-footer verzoken-footer-height">

        {
          bestuurList ? <> <CardFooter className="chat-layout-footer__inner"><div className="d-flex gap-3 mr-4">
            <div>
              <select className={`form-select`} value={communityId} onChange={(e) => { setCommunityId(Number(e.target.value)) }}>
                {communityList?.map(item => {
                  return <option key={item.iCommunityId} value={item.iCommunityId}>{item.name}</option>
                })}
              </select>
            </div>
            <Button
              outline
              className="btn-outline-primary-blue btn-color-style py-2 px-3 d-flex gap-2 align-items-center"
              type="button"
              color="primary"
              onClick={() => {
                setOpenFileUploadModal(true);
              }}
            >
              Protocol <AddIcon />
            </Button>

            <PDFPopup isOpen={openFileUploadModal} togglePopup={togglePDFmodle} file={file} setFile={setFile} doc={file} handleFileSave={handleFileSave} isPdfUploading={isPdfUploading} classes="document-modal-style modal-centered" />
            {openSearchModal && <ReporterModal setModal={setOpenSearchModal} modal={openSearchModal} addOrDeleteMelderHandler={addOrDeleteMelderHandler} reporterData={reporterData} frefreshReporterData={refreshReporterData} isReporterLoading={isReporterLoading} />}

          </div></CardFooter></> : null
        }
      </div>
    </Fragment>
  )
}

export default Besturen